import { inject, Injectable } from '@angular/core';
import { collection, doc, Firestore, serverTimestamp, setDoc } from '@angular/fire/firestore';
import { BaseModel } from '../../global/models';
import { FirestoreBase } from '../db/firestore-base';

/**
 * Low-level service to persist a document in any Firestore collection.
 * Extends FirestoreBase so that `createdAt`, `updatedAt`, and ID handling
 * are consistent with the rest of the domain services.
 */
@Injectable({ providedIn: 'root' })
export class FileDbService extends FirestoreBase<BaseModel> {
  constructor() {
    super('_file-db', inject(Firestore));
  }

  /**
   * Creates a new document in any Firestore collection.
   * Always sets `createdAt` and `updatedAt` server timestamps.
   * Deliberately never calls `upsert()` — that would skip `createdAt` when an ID is supplied.
   * @param data - The document payload.
   * @param collectionPath - Full Firestore collection path.
   * @param id - Optional document ID. If omitted, one is auto-generated via `createId()`.
   * @returns Promise resolving to the document ID of the newly created document.
   */
  async save(data: Record<string, unknown>, collectionPath: string, id?: string): Promise<string> {
    return this.run(async () => {
      const colRef = collection(this.firestore, collectionPath);
      const docId = id ?? this.createId();
      const docRef = doc(colRef, docId);

      // TODO: juntar esto con el this.create().
      const payload = {
        ...data,
        id: docId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(docRef, payload);
      return docId;
    });
  }
}
